import React, { useState } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Transparent_Logo_Asset as weretradeLogo } from '../shared/assets-compat';

const ErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const [copied, setCopied] = useState(false);
  let status = 500;
  let statusText = 'Internal Server Error';
  let message = 'Ein unerwarteter Fehler ist aufgetreten.';
  let is404 = false;

  if (isRouteErrorResponse(error)) {
    status = error.status;
    statusText = error.statusText;
    is404 = error.status === 404;
    message = is404
      ? 'Die von Ihnen gesuchte Seite konnte nicht gefunden werden. Möglicherweise wurde sie verschoben oder existiert nicht mehr.'
      : error.data?.message || error.data || 'Ein Anwendungsfehler ist aufgetreten.';
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  const handleCopy = () => {
    const textToCopy = error instanceof Error 
      ? error.stack || error.message 
      : typeof error === 'string' 
        ? error 
        : JSON.stringify(error, null, 2);
    
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="error-boundary-wrapper">
      {/* Dynamic Font and Style Loading */}
      <style>{`
        /* Kein @import mehr, 05.09.2026. Hier stand ein Aufruf an
           fonts.googleapis.com fuer Outfit und JetBrains Mono -- die einzige
           Stelle der ganzen App, die Schriften aus dem Netz laedt.

           Auf einer FEHLERSEITE ist das die falsche Abhaengigkeit: sie feuert
           genau dann, wenn schon etwas kaputt ist, und wenn die Ursache ein
           Netzfehler war, kommt sie nie an. Ein @import in einem nachtraeglich
           eingefuegten style-Block wird ausserdem spaet entdeckt und haelt das
           Rendern der Fehlerseite auf.

           Verloren geht dabei wenig: die uebrige App laedt ueberhaupt keine
           Webschrift. styles.css nennt zwar "Inter", aber weder index.html
           noch ein @font-face laedt sie -- gerendert wird seit jeher die
           Systemschrift. Nur die Fehlerseite wollte eine eigene.

           Stattdessen die Schrift-Token des Designsystems, mit einer echten
           Systemschrift als Rueckfall IM var() -- falls das Blatt im
           Fehlerfall selbst nicht geladen ist. */

        .error-boundary-wrapper {
          /* Theme Tokens - LIGHT Mode (Default) */
          --eb-bg: #f4f6f9;
          --eb-radial-bg: radial-gradient(circle at 0% 0%, rgba(240, 48, 120, 0.08) 0%, transparent 40%),
                           radial-gradient(circle at 100% 0%, rgba(48, 72, 120, 0.08) 0%, transparent 40%),
                           radial-gradient(circle at 100% 100%, rgba(59, 130, 246, 0.06) 0%, transparent 45%),
                           radial-gradient(circle at 0% 100%, rgba(168, 85, 247, 0.06) 0%, transparent 45%),
                           #f8fafc;
          --eb-card-bg: rgba(255, 255, 255, 0.55);
          --eb-card-border: 1px solid rgba(255, 255, 255, 0.5);
          --eb-text-color: #0f172a;
          --eb-muted-color: #475569;
          --eb-border-color: rgba(15, 23, 42, 0.08);
          --eb-shadow: 0 4px 30px rgba(0, 0, 0, 0.03),
                       0 30px 60px rgba(15, 23, 42, 0.08),
                       inset 0 1px 1px rgba(255, 255, 255, 0.8);
          /* Feste Werte statt Token, mit Absicht: dieser Baustein muss auch
             anzeigen koennen, wenn das App-CSS nicht geladen hat. Die Zahlen
             sind die am Logo gemessenen; Herleitung im CHANGELOG zu 3.2.0.
             Bewusst ohne weitere Hex-Werte im Text: dieser Block ist eine
             Zeichenkette und wird mit ausgeliefert. */
          --eb-accent-color: #f03078;
          --eb-btn-text: #ffffff;
          --eb-btn-secondary-bg: rgba(255, 255, 255, 0.6);
          --eb-btn-secondary-hover: rgba(255, 255, 255, 0.95);
          --eb-btn-secondary-border: rgba(15, 23, 42, 0.06);

          /* Layout & Transitions */
          min-height: 100vh;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background: var(--eb-radial-bg);
          font-family: var(--font-sans, system-ui, -apple-system, "Segoe UI", sans-serif);
          color: var(--eb-text-color);
          text-align: center;
          position: relative;
          overflow: hidden;
          box-sizing: border-box;
          transition: background-color 0.4s ease, color 0.4s ease;
        }

        /* Dark Mode CSS Overrides */
        html[data-theme="dark"] .error-boundary-wrapper {
          --eb-bg: #090d12;
          --eb-radial-bg: radial-gradient(circle at 0% 0%, rgba(240, 48, 120, 0.12) 0%, transparent 35%),
                           radial-gradient(circle at 100% 0%, rgba(48, 72, 120, 0.1) 0%, transparent 35%),
                           radial-gradient(circle at 100% 100%, rgba(59, 130, 246, 0.08) 0%, transparent 40%),
                           radial-gradient(circle at 0% 100%, rgba(168, 85, 247, 0.08) 0%, transparent 40%),
                           #070a0e;
          --eb-card-bg: rgba(13, 19, 28, 0.45);
          --eb-card-border: 1px solid rgba(255, 255, 255, 0.04);
          --eb-text-color: #f8fafc;
          --eb-muted-color: #94a3b8;
          --eb-border-color: rgba(255, 255, 255, 0.06);
          --eb-shadow: 0 4px 30px rgba(0, 0, 0, 0.2),
                       0 30px 70px rgba(0, 0, 0, 0.45),
                       inset 0 1px 1px rgba(255, 255, 255, 0.03);
          --eb-btn-secondary-bg: rgba(255, 255, 255, 0.03);
          --eb-btn-secondary-hover: rgba(255, 255, 255, 0.06);
          --eb-btn-secondary-border: rgba(255, 255, 255, 0.06);
        }

        @media (prefers-color-scheme: dark) {
          html:not([data-theme="light"]) .error-boundary-wrapper {
            --eb-bg: #090d12;
            --eb-radial-bg: radial-gradient(circle at 0% 0%, rgba(240, 48, 120, 0.12) 0%, transparent 35%),
                             radial-gradient(circle at 100% 0%, rgba(48, 72, 120, 0.1) 0%, transparent 35%),
                             radial-gradient(circle at 100% 100%, rgba(59, 130, 246, 0.08) 0%, transparent 40%),
                             radial-gradient(circle at 0% 100%, rgba(168, 85, 247, 0.08) 0%, transparent 40%),
                             #070a0e;
            --eb-card-bg: rgba(13, 19, 28, 0.45);
            --eb-card-border: 1px solid rgba(255, 255, 255, 0.04);
            --eb-text-color: #f8fafc;
            --eb-muted-color: #94a3b8;
            --eb-border-color: rgba(255, 255, 255, 0.06);
            --eb-shadow: 0 4px 30px rgba(0, 0, 0, 0.2),
                         0 30px 70px rgba(0, 0, 0, 0.45),
                         inset 0 1px 1px rgba(255, 255, 255, 0.03);
            --eb-btn-secondary-bg: rgba(255, 255, 255, 0.03);
            --eb-btn-secondary-hover: rgba(255, 255, 255, 0.06);
            --eb-btn-secondary-border: rgba(255, 255, 255, 0.06);
          }
        }

        /* Glassmorphic Container Card */
        .eb-glass-card {
          max-width: 540px;
          width: 100%;
          border-radius: 24px;
          background: var(--eb-card-bg);
          border: var(--eb-card-border);
          box-shadow: var(--eb-shadow);
          backdrop-filter: blur(40px) saturate(180%);
          -webkit-backdrop-filter: blur(40px) saturate(180%);
          padding: 44px 36px;
          position: relative;
          z-index: 2;
          animation: eb-fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          transition: border-color 0.3s, background-color 0.3s, box-shadow 0.3s;
          box-sizing: border-box;
        }

        /* Brand & Logo styling */
        .eb-brand-header {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-bottom: 28px;
          opacity: 0;
          z-index: 2;
          animation: eb-fade-in 0.6s ease 0.2s forwards;
          text-decoration: none;
        }
        
        .eb-brand-logo {
          height: 36px;
          width: auto;
          filter: drop-shadow(0 4px 10px rgba(0,0,0,0.06));
          transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .eb-brand-header:hover .eb-brand-logo {
          transform: rotate(10deg) scale(1.1);
        }

        .eb-brand-text {
          font-weight: 800;
          font-size: 20px;
          letter-spacing: -0.01em;
          color: var(--eb-text-color);
          font-family: var(--font-sans, system-ui, -apple-system, "Segoe UI", sans-serif);
        }

        /* Status Badge */
        .eb-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-transform: uppercase;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.16em;
          color: var(--eb-accent-color);
          background: rgba(240, 48, 120, 0.07);
          border: 1px solid rgba(240, 48, 120, 0.15);
          padding: 6px 14px;
          border-radius: 99px;
          margin-bottom: 22px;
          box-shadow: 0 4px 12px rgba(240, 48, 120, 0.02);
        }

        .eb-status-badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: var(--eb-accent-color);
          animation: eb-pulse-dot 1.5s infinite;
        }

        @keyframes eb-pulse-dot {
          0% { transform: scale(0.85); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(0.85); opacity: 0.5; }
        }

        /* Typography */
        .eb-title {
          font-size: 32px;
          font-weight: 800;
          margin: 0 0 14px 0;
          color: var(--eb-text-color);
          line-height: 1.15;
          letter-spacing: -0.03em;
        }

        .eb-message {
          color: var(--eb-muted-color);
          font-size: 16px;
          line-height: 1.6;
          margin: 0 0 32px 0;
          text-wrap: pretty;
          font-weight: 500;
        }

        /* Premium Buttons */
        .eb-btn-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .eb-btn-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .eb-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 14px 24px;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          text-decoration: none;
          outline: none;
          box-sizing: border-box;
          border: none;
        }

        .eb-btn:focus-visible {
          outline: 3px solid var(--eb-accent-color);
          outline-offset: 2px;
        }

        .eb-btn-primary {
          background: linear-gradient(135deg, #f03078, #FF5FA7);
          color: #ffffff !important;
          box-shadow: 0 8px 24px rgba(240, 48, 120, 0.2), 
                      inset 0 1px 0 rgba(255, 255, 255, 0.2);
          position: relative;
          overflow: hidden;
        }

        .eb-btn-primary::after {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
          transition: left 0.5s ease;
        }

        .eb-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(240, 48, 120, 0.3), 
                      0 4px 8px rgba(240, 48, 120, 0.15);
        }

        .eb-btn-primary:hover::after {
          left: 100%;
        }

        .eb-btn-primary:active {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(240, 48, 120, 0.2);
        }

        .eb-btn-secondary {
          background: var(--eb-btn-secondary-bg);
          color: var(--eb-text-color);
          border: 1px solid var(--eb-btn-secondary-border);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
        }

        .eb-btn-secondary:hover {
          background: var(--eb-btn-secondary-hover);
          border-color: var(--eb-text-color);
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(0, 0, 0, 0.04);
        }

        .eb-btn-secondary:active {
          transform: translateY(0);
        }

        /* Accordion Details */
        .eb-details {
          margin-top: 28px;
          text-align: left;
          border-top: 1px solid var(--eb-border-color);
          padding-top: 20px;
          width: 100%;
        }

        .eb-details-summary {
          cursor: pointer;
          color: var(--eb-muted-color);
          font-size: 13px;
          font-weight: 600;
          user-select: none;
          outline: none;
          transition: color 0.2s;
        }

        .eb-details-summary:hover {
          color: var(--eb-text-color);
        }

        .eb-details-container {
          position: relative;
          margin-top: 12px;
        }

        .eb-copy-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: var(--eb-btn-secondary-bg);
          border: 1px solid var(--eb-btn-secondary-border);
          color: var(--eb-text-color);
          font-family: inherit;
          font-size: 11px;
          font-weight: 700;
          padding: 5px 10px;
          border-radius: 6px;
          cursor: pointer;
          z-index: 10;
          transition: all 0.2s;
        }

        .eb-copy-btn:hover {
          background: var(--eb-btn-secondary-hover);
          border-color: var(--eb-text-color);
        }

        .eb-details-content {
          margin: 0;
          padding: 16px;
          padding-top: 42px;
          border-radius: 12px;
          background: rgba(0, 0, 0, 0.03);
          border: 1px solid var(--eb-border-color);
          font-size: 12px;
          font-family: var(--font-mono, ui-monospace, "SF Mono", Consolas, monospace);
          color: #ef4444;
          overflow-x: auto;
          white-space: pre-wrap;
          max-height: 180px;
          line-height: 1.5;
        }

        html[data-theme="dark"] .eb-details-content {
          background: rgba(0, 0, 0, 0.2);
          color: #f87171;
        }

        /* Background grid overlay */
        .eb-grid-overlay {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(15, 23, 42, 0.012) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(15, 23, 42, 0.012) 1px, transparent 1px);
          background-size: 40px 40px;
          background-position: center center;
          pointer-events: none;
          z-index: 1;
        }

        html[data-theme="dark"] .eb-grid-overlay {
          background-image: linear-gradient(rgba(255, 255, 255, 0.01) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255, 255, 255, 0.01) 1px, transparent 1px);
        }

        /* Floating background decorative blobs */
        .eb-bg-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          z-index: 0;
          pointer-events: none;
          opacity: 0.55;
          animation: eb-orb-float 20s ease-in-out infinite alternate;
        }
        .eb-bg-orb-1 {
          width: 350px;
          height: 350px;
          top: 10%;
          left: 5%;
          background: rgba(240, 48, 120, 0.12);
        }
        .eb-bg-orb-2 {
          width: 400px;
          height: 400px;
          bottom: 10%;
          right: 5%;
          background: rgba(48, 72, 120, 0.09);
          animation-delay: -6s;
        }

        html[data-theme="dark"] .eb-bg-orb-1 {
          background: rgba(240, 48, 120, 0.18);
        }
        html[data-theme="dark"] .eb-bg-orb-2 {
          background: rgba(48, 72, 120, 0.14);
        }

        @keyframes eb-orb-float {
          0% { transform: translate(0, 0) scale(1); }
          100% { transform: translate(50px, 80px) scale(1.15); }
        }

        /* Animations */
        @keyframes eb-fade-up {
          0% { opacity: 0; transform: translateY(30px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes eb-fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }

        /* SVG Animation classes */
        .svg-float {
          animation: svg-float 5s ease-in-out infinite;
        }

        @keyframes svg-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(1.5deg); }
        }

        .svg-pulse-glow {
          animation: svg-pulse-glow 3s ease-in-out infinite;
        }

        @keyframes svg-pulse-glow {
          0%, 100% { opacity: 0.15; stroke-width: 1.5px; }
          50% { opacity: 0.45; stroke-width: 3.5px; }
        }

        .svg-warning-vibrate {
          animation: svg-warning-vibrate 5s ease-in-out infinite;
        }

        @keyframes svg-warning-vibrate {
          0%, 100% { transform: rotate(0deg); }
          45% { transform: rotate(0deg) scale(1); }
          47% { transform: rotate(-2deg) scale(1.02); }
          49% { transform: rotate(2deg) scale(1.02); }
          51% { transform: rotate(-2deg) scale(1.02); }
          53% { transform: rotate(1deg) scale(1); }
          55% { transform: rotate(0deg); }
        }

        .svg-search-glass {
          animation: svg-search-glass 6s ease-in-out infinite;
          transform-origin: 86px 86px;
        }

        @keyframes svg-search-glass {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          30% { transform: translate(-8px, -4px) rotate(-6deg); }
          70% { transform: translate(6px, 8px) rotate(8deg); }
        }

        .svg-sparkle {
          animation: svg-sparkle 4s ease-in-out infinite;
        }
        .svg-sparkle-1 { animation-delay: 0.5s; }
        .svg-sparkle-2 { animation-delay: 2s; }

        @keyframes svg-sparkle {
          0%, 100% { opacity: 0.2; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>

      {/* Decorative Orbs & Grid in background */}
      <div className="eb-bg-orb eb-bg-orb-1" />
      <div className="eb-bg-orb eb-bg-orb-2" />
      <div className="eb-grid-overlay" />

      <div style={{ maxWidth: '540px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
        {/* Brand Header */}
        <a href="/" className="eb-brand-header">
          <img src={weretradeLogo} alt="weretrade IT" className="eb-brand-logo" />
          <span className="eb-brand-text">weretrade IT</span>
        </a>

        {/* Refined Glass Card */}
        <main className="eb-glass-card">
          {/* Animated SVG Graphic */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }} className="svg-float">
            {is404 ? (
              <svg width="140" height="140" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Glowing Outer Ring */}
                <circle cx="70" cy="70" r="58" fill="none" stroke="#f03078" className="svg-pulse-glow" />
                <circle cx="70" cy="70" r="50" fill="none" stroke="var(--eb-accent-color)" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 6" style={{ opacity: 0.6 }} />
                
                {/* Floating Document/Sheet */}
                <rect x="42" y="38" width="56" height="64" rx="10" stroke="var(--eb-text-color)" strokeWidth="3" fill="var(--eb-card-bg)" />
                <path d="M52 50H72" stroke="var(--eb-muted-color)" strokeWidth="3" strokeLinecap="round" />
                <path d="M52 60H88" stroke="var(--eb-muted-color)" strokeWidth="3" strokeLinecap="round" />
                <path d="M52 70H80" stroke="var(--eb-muted-color)" strokeWidth="3" strokeLinecap="round" />
                
                {/* Searching Magnifying Glass */}
                <g className="svg-search-glass">
                  <circle cx="86" cy="86" r="16" fill="rgba(48, 72, 120, 0.15)" stroke="#304878" strokeWidth="3.5" />
                  <path d="M97 97L112 112" stroke="#304878" strokeWidth="4.5" strokeLinecap="round" />
                  <path d="M78 82C80 80 84 80 86 82" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" style={{ opacity: 0.8 }} />
                </g>
                
                {/* Animated Decorative Sparkles */}
                <path d="M30 40 L33 43 L30 46 L27 43 Z" fill="#304878" className="svg-sparkle svg-sparkle-1" />
                <path d="M110 50 L112 52 L110 54 L108 52 Z" fill="#f03078" className="svg-sparkle svg-sparkle-2" />
                
                {/* Small question mark */}
                <text x="46" y="91" fill="var(--eb-accent-color)" fontSize="16" fontWeight="bold" fontFamily="sans-serif">?</text>
              </svg>
            ) : (
              <svg width="140" height="140" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg" className="svg-warning-vibrate">
                {/* Glowing Outer Ring */}
                <circle cx="70" cy="70" r="58" fill="none" stroke="#304878" className="svg-pulse-glow" />
                <circle cx="70" cy="70" r="50" fill="none" stroke="#304878" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 6" style={{ opacity: 0.6 }} />
                
                {/* Warning Triangle */}
                <path d="M70 32L105 92H35L70 32Z" stroke="var(--eb-text-color)" strokeWidth="3.5" fill="var(--eb-card-bg)" strokeLinejoin="round" />
                
                {/* Exclamation point inside warning */}
                <path d="M70 48V70" stroke="#304878" strokeWidth="4.5" strokeLinecap="round" />
                <circle cx="70" cy="79" r="2.5" fill="#304878" />

                {/* Floating particles */}
                <circle cx="34" cy="50" r="2.5" fill="#f03078" className="svg-sparkle svg-sparkle-1" />
                <circle cx="106" cy="70" r="2" fill="#f03078" className="svg-sparkle svg-sparkle-2" />
              </svg>
            )}
          </div>

          {/* Status Badge */}
          <div className="eb-status-badge">
            <span className="eb-status-badge-dot" />
            {is404 ? '404 - Not Found' : `Error ${status}`}
          </div>

          {/* Typography Content */}
          <h1 className="eb-title">
            {is404 ? 'Seite nicht gefunden' : statusText || 'Systemfehler'}
          </h1>
          <p className="eb-message">
            {message}
          </p>

          {/* Action Buttons */}
          <div className="eb-btn-group">
            <a href="/" className="eb-btn eb-btn-primary">
              🏠 Zur Startseite
            </a>
            <div className="eb-btn-row">
              <button onClick={() => window.history.back()} className="eb-btn eb-btn-secondary">
                ↩️ Zurück
              </button>
              <a href="mailto:kontakt@weretrade.com" className="eb-btn eb-btn-secondary">
                ✉️ Support
              </a>
            </div>
          </div>

          {/* Technical details accordion for devs */}
          {!is404 && (
            <div className="eb-details">
              <details>
                <summary className="eb-details-summary">
                  Technische Fehlerdetails einblenden
                </summary>
                <div className="eb-details-container">
                  <button onClick={handleCopy} className="eb-copy-btn">
                    {copied ? '✓ Kopiert!' : '🗐 Kopieren'}
                  </button>
                  <pre className="eb-details-content">
                    {error instanceof Error 
                      ? error.stack || error.message 
                      : typeof error === 'string'
                        ? error
                        : JSON.stringify(error, null, 2)}
                  </pre>
                </div>
              </details>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ErrorBoundary;
